import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Col, Divider, Flex, Modal, Row, Select, Skeleton, Space, Spin, Typography } from 'antd';
import { MinusCircleFilled, PlusCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Actions
import { search_routes_minis, search_routes_updates } from '../../../redux/actions/routesActions';

// Utils
import isEmpty from '../../../utils/isEmpty';
import { get_load_weekday_desc } from '../../../utils/get_status_desc';

const LoadRouteInfo = (props) => {
	const { openModal, node, onCancel } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const modalContentRef = useRef(null);

	const { isAdminUser } = useSelector(state => state.auth);

	const { file_info, file_history, selected_node } = useSelector(state => state.self);

	const { routes_loading, routes_updates, routes_minis_loading, routes_minis } = useSelector(state => state.routes);

	const [historyId, setHistoryId] = useState('');

	const onChangeHistory =	(historyId) => {
		const routeFilters = { 
			organization: file_info?.organization?._id?.$oid,
			workapace: file_info?.workspace?._id?.$oid,
			...(file_info?.load && { load: file_info?.load?._id?.$oid }),
			file: file_info?._id?.$oid,
			...(selected_node?.route ? { route: selected_node?.route } : { code: selected_node?.code }),
			history: historyId
		}
		dispatch(search_routes_updates(routeFilters));
		dispatch(search_routes_minis({
			...routeFilters,
			cycle: file_info?.cycle?._id?.$oid
		}));
	}

	useEffect(() => {
		setHistoryId(file_history.history_latest);
	}, [file_history]);

	useEffect(() => {
		if (historyId !== '' && !isEmpty(selected_node)) {
			onChangeHistory(historyId);
		}
	}, [historyId, selected_node]);

	useEffect(() => {
		if (openModal) {
			setTimeout(() => {
				if (modalContentRef.current) {
					modalContentRef.current.scrollTop = 0; // Ensure the scroll starts at the top
				}
			}, 100); // Wait 100ms to ensure the modal is fully rendered
		}
	}, [openModal]); // Runs only when the modal opens

	const locationType = {
		none: {
			color: null,
			icon: null
		},
		extra: {
			color: '#b7eb8f',
			icon: <PlusCircleFilled style={{ color: '#52c41a' }} />
		},
		missing: {
			color: '#ffa39e',
			icon: <MinusCircleFilled style={{ color: '#f5222d' }} />
		}
	};

	const formatDate = (epoch) => {
		return new Intl.DateTimeFormat(localStorage.getItem('i18nextLng'), { weekday: 'long', day: 'numeric', month: 'long' }).format(epoch);
	}

	return (
		<Modal
			title={`${t('filters.route')} ${node?.title?.props?.node?.title}`}
			centered
			open={openModal}
			onCancel={onCancel}
			footer={null}
			width={750}
		>
			<div style={{ maxHeight: '60vh', overflowY: 'auto' }} ref={modalContentRef}>
				<Skeleton active loading={routes_loading}>
					{/* History selector */}
					{isAdminUser &&
						<div>
							<Typography.Text level={4} copyable={{ text: historyId, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
								{t('loadStatusDetails.validationHistory.title')}
							</Typography.Text>
							<Select
								options={file_history.history_options}
								value={historyId}
								placeholder={t('loadStatusDetails.validationHistory.selectHistoryPlaceholder')}
								style={{ width: '100%', marginBottom: '1rem' }}
								onChange={(value) => setHistoryId(value)}
							/>
						</div>
					}
					{/* Number of locations */}
					<Flex justify='flex-end' style={{ margin: '0.5rem 1rem 1rem' }}>
						<Space size='large'>
							{routes_updates.filter(location => location.type === 'missing').length !== 0 &&
								<Space size={6}>
									<MinusCircleFilled style={{ color: '#f5222d' }} />
									<Typography.Text type='secondary'>{t('loadStatusDetails.routesSummary.removedLocations')}: </Typography.Text>
									<Typography.Text type='secondary'>{routes_updates.filter(location => location.type === 'missing').length}</Typography.Text>
								</Space>
							}
							{routes_updates.filter(location => location.type === 'extra').length !== 0 &&
								<Space size={6}>
									<PlusCircleFilled style={{ color: '#52c41a' }} />
									<Typography.Text type='secondary'>{t('loadStatusDetails.routesSummary.addedLoacations')}: </Typography.Text>
									<Typography.Text type='secondary'>{routes_updates.filter(location => location.type === 'extra').length}</Typography.Text>
								</Space>
							}
						</Space>
					</Flex>
					{/* Routes updates */}
					<Row>
						{routes_updates.map((route) => (
							<Col lg={4} md={4} sm={6} xs={8} key={route.code}>
								<Typography.Paragraph>
									{locationType[route.type].icon} {route.code}
								</Typography.Paragraph>
							</Col>
						))}
					</Row>
					{/* Routes minis */}
					<Divider />
					{routes_minis.map((day, idx) => (
						<Fragment key={day?._id?.$oid}>
							<Typography.Paragraph
								strong
								copyable={{ text: day._id.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}
							>
								{day?.weekday !== undefined
								? get_load_weekday_desc(day.weekday)
								: `${formatDate(day?.start?.$date).charAt(0).toUpperCase() + formatDate(day?.start?.$date).slice(1)} - ${formatDate(day?.end?.$date).charAt(0).toUpperCase() + formatDate(day?.end?.$date).slice(1)}`
							}
							</Typography.Paragraph>
							<Row>
								{day?.locations.map((location) => (
									<Col lg={4} md={4} sm={6} xs={8} key={location}>
										<Typography.Paragraph>{location}</Typography.Paragraph>
									</Col>
								))}
							</Row>
							{idx !== routes_minis.length - 1 && <Divider />}
						</Fragment>
					))}
				</Skeleton>
			</div>
		</Modal>
	)
}

export default LoadRouteInfo;
